import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Dropdown, Modal, Segmented, Skeleton, Space, Tooltip, message, Typography } from 'antd';
import {
    EditOutlined, HolderOutlined, ArrowUpOutlined, ArrowDownOutlined, ReloadOutlined, UndoOutlined, CheckOutlined,
    RightOutlined, CloseOutlined, PlusOutlined, DownOutlined, DeleteOutlined,
} from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import PageHeader from '../../shared/components/PageHeader';
import { useAuth } from '../../shared/hooks/useAuth';
import { userPath } from '../../shared/config/paths';
import dashboardService from '../services/dashboardService';
import onboardingService from '../services/onboardingService';
import authService from '../../shared/services/authService';
import {
    ALL_KEYS, DEFAULT_SIZE, SIZES, SIZE_LABEL, VIEWS, WIDGET_TITLES, mergeLayout, readLocal, viewOf, writeLocal,
} from './dashboard/layoutStore';
import { WIDGETS, EmptyProduct } from './dashboard/widgets';
import './dashboard/dashboard.css';
import AutoRenewNudge from '../components/AutoRenewNudge';
import { t, useTranslation } from '../../shared/i18n';
import { TicketGrid, useUnseenCoupons, couponUsePath } from '../components/CouponTicket';
import { setPendingCoupon } from '../utils/pendingCoupon';
import CampaignBanner from '../components/CampaignBanner';
import { ONB_PRODUCTS } from './dashboard/onboardingProducts';

const { Text } = Typography;

const tokenOf = (w) => {
    const v = viewOf(w);
    return v ? `${w.key}:${v}` : w.key;
};

/**
 * Loads widget data per (key, view) in a few batches; every key keeps its own {loading, ok, data, view, error}.
 * Only the visible card's current view is fetched; switching view fetches that view lazily.
 */
const useWidgetData = (widgets, cdnRange) => {
    const [state, setState] = useState({});
    const seq = useRef({});

    const load = useCallback(async (list, { refresh = false, range } = {}) => {
        const items = list.filter((w) => WIDGETS[w.key]?.fetch);
        if (!items.length) return;
        const groups = {};
        items.forEach((w) => {
            const g = WIDGETS[w.key].group || w.key;
            (groups[g] = groups[g] || []).push(w);
        });
        setState((s) => {
            const n = { ...s };
            items.forEach((w) => { n[w.key] = { ...(s[w.key] || {}), loading: true, want: viewOf(w) }; });
            return n;
        });
        await Promise.all(Object.values(groups).map(async (ws) => {
            const tokens = ws.map(tokenOf);
            const sk = ws.map((w) => w.key).join();
            const id = (seq.current[sk] || 0) + 1;
            seq.current[sk] = id;
            let res = null;
            let failed = false;
            try {
                res = await dashboardService.getWidgets(tokens, { cdn_range: range, refresh });
            } catch (e) {
                failed = true;
            }
            if (seq.current[sk] !== id) return; // stale response
            setState((s) => {
                const n = { ...s };
                ws.forEach((w) => {
                    const r = res?.[w.key];
                    const view = viewOf(w);
                    if (s[w.key]?.want && s[w.key].want !== view) return; // the user switched view meanwhile
                    n[w.key] = failed || !r
                        ? { loading: false, ok: false, error: '加载失败', view }
                        : { loading: false, ok: !!r.ok, data: r.data, view, stale: !!r.stale, error: r.ok ? null : (r.error || '加载失败') };
                });
                return n;
            });
        }));
    }, []);

    const sig = widgets.map(tokenOf).join(',');
    useEffect(() => {
        const missing = widgets.filter((w) => WIDGETS[w.key]?.fetch && (!state[w.key] || (state[w.key].view !== viewOf(w) && state[w.key].want !== viewOf(w))));
        if (missing.length) load(missing, { range: cdnRange });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sig]);

    return { state, load };
};

const useViewportWidth = () => {
    const [w, setW] = useState(() => window.innerWidth);
    useEffect(() => {
        const on = () => setW(window.innerWidth);
        window.addEventListener('resize', on);
        return () => window.removeEventListener('resize', on);
    }, []);
    return w;
};

/** Column span per size at a viewport width (mirrors dashboard.css breakpoints). */
const spanOf = (size, vw) => {
    if (vw < 768) return 12;
    if (size === 'full') return 12;
    if (size === 'third') return vw < 1200 ? 6 : 4;
    return 6;
};

/** View mode: widen the last card of each row to close the gap; edit mode keeps the chosen sizes. */
const packSpans = (items, vw) => {
    const spans = items.map((it) => spanOf(it.size, vw));
    let used = 0;
    let rowLast = -1;
    spans.forEach((s, i) => {
        if (used + s > 12) {
            if (rowLast >= 0) spans[rowLast] += 12 - used;
            used = 0;
        }
        used += s;
        rowLast = i;
    });
    if (rowLast >= 0) spans[rowLast] += 12 - used;
    return spans;
};

/** Per-widget render boundary: one broken widget shows a line, never blanks the page. */
class WidgetBoundary extends React.Component {
    constructor(props) { super(props); this.state = { err: false }; }

    static getDerivedStateFromError() { return { err: true }; }

    componentDidCatch(error) { console.warn('[dashboard] widget render failed:', error); } // eslint-disable-line no-console

    componentDidUpdate(prev) { if (prev.resetKey !== this.props.resetKey && this.state.err) this.setState({ err: false }); } // eslint-disable-line react/no-did-update-set-state

    render() { return this.state.err ? <div className="dw-error"><span>{t('Failed to display')}</span></div> : this.props.children; }
}

const WidgetBody = ({ w, st, ctx, onRetry }) => {
    const { t } = useTranslation();
    const def = WIDGETS[w.key];
    const view = viewOf(w);
    if (!def.fetch) return <def.Body ctx={ctx} size={w.size} />;
    if (!st || (st.loading && (!st.data || st.view !== view))) return <Skeleton active paragraph={{ rows: 3 }} title={false} />;
    if (!st.ok) {
        return (
            <div className="dw-error">
                <Text type="secondary">{t(st.error || '加载失败')}</Text>
                <Button size="small" icon={<ReloadOutlined />} onClick={onRetry}>{t('Retry')}</Button>
            </div>
        );
    }
    if (def.product && def.empty && st.data && def.empty(st.data)) {
        return def.Showcase ? <def.Showcase data={st.data} ctx={ctx} size={w.size} /> : <EmptyProduct product={def.product} />;
    }
    return <def.Body data={st.data || {}} ctx={ctx} size={w.size} view={st.view || view} />;
};

/** Compact view switch: segmented on roomy cards, a small menu otherwise (phones, 1/3 cards, many views). */
const ViewSwitch = ({ w, vw, onChange }) => {
    const { t } = useTranslation();
    const views = VIEWS[w.key];
    if (!views || views.length < 2) return null;
    const cur = viewOf(w);
    const roomy = vw >= 768 && (w.size === 'full' || (w.size === 'half' && views.length <= 3 && vw >= 1200));
    if (roomy) {
        return <Segmented size="small" value={cur} onChange={onChange} options={views.map(([v, l]) => ({ value: v, label: t(l) }))} />;
    }
    const label = views.find(([v]) => v === cur)?.[1] || views[0][1];
    return (
        <Dropdown
            trigger={['click']}
            menu={{ items: views.map(([v, l]) => ({ key: v, label: t(l) })), selectable: true, selectedKeys: [cur], onClick: ({ key }) => onChange(key) }}
        >
            <Button size="small" type="text" className="dw-view-btn">{t(label)} <DownOutlined /></Button>
        </Dropdown>
    );
};

/**
 * First login, one screen: "what do you want to do first?". The pick becomes the checklist's focus product (onDone(key)).
 * Also: the welcome modal also carries new coupons as tickets on top (click one to use it) (the layout skips its own ticket
 * while the welcome is pending, so the two never stack). Picking a product the coupon covers goes straight to
 * its purchase page with ?coupon= prefilled. Any close marks the coupons seen.
 */
const WelcomeModal = ({ open, onDone }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [coupons, markAllSeen] = useUnseenCoupons(open);
    const done = (focus) => { markAllSeen(); onDone(typeof focus === 'string' ? focus : null); };
    const go = (to, coupon, focus) => {
        if (coupon) setPendingCoupon(coupon.code);
        done(focus);
        navigate(userPath(to));
    };
    const pick = (c) => {
        const cp = coupons.find((x) => x.scope_type === c.key);
        go(cp ? couponUsePath(cp) : c.to, cp, c.key);
    };
    return (
        <Modal open={open} title={t('What do you want to do first?')} footer={null} onCancel={done} width={680} destroyOnClose>
            {coupons.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <TicketGrid coupons={coupons} onUse={(c) => go(couponUsePath(c), c)} />
                </div>
            )}
            <div className="onb-welcome-grid">
                {ONB_PRODUCTS.map((c) => (
                    <button type="button" key={c.key} className="onb-choice" onClick={() => pick(c)}>
                        <span className="onb-choice-icon">{c.icon}</span>
                        <span className="onb-choice-name">{t(c.name)}</span>
                        <Text type="secondary" style={{ fontSize: 12 }}>{t(c.text)}</Text>
                    </button>
                ))}
            </div>
            <div style={{ textAlign: 'right', marginTop: 12 }}>
                <Button type="link" onClick={() => done()}>{t('Skip')}</Button>
            </div>
        </Modal>
    );
};

/** Accounts past the checklist window that still use nothing: one dismissible strip of products. */
const StartStrip = ({ onPick, onClose }) => {
    const { t } = useTranslation();
    return (
        <div className="onb-strip">
            <Text strong>{t('Not started yet? Pick a product:')}</Text>
            <div className="onb-strip-items">
                {ONB_PRODUCTS.map((p) => (
                    <Button key={p.key} size="small" icon={p.icon} onClick={() => onPick(p)}>{t(p.name)}</Button>
                ))}
            </div>
            <Tooltip title={t('Hide')}><Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} aria-label={t('Hide')} /></Tooltip>
        </div>
    );
};

const Dashboard = () => {
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const uid = currentUser?.id;
    const [layout, setLayout] = useState(() => mergeLayout(readLocal(uid)));
    const [persisted, setPersisted] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [cdnRange, setCdnRange] = useState('24h');
    const [dragKey, setDragKey] = useState(null);
    const [overKey, setOverKey] = useState(null);
    const vw = useViewportWidth();
    const viewSave = useRef(null);
    const [onb, setOnb] = useState(null);
    const [trial, setTrial] = useState(false);
    const [welcomeOpen, setWelcomeOpen] = useState(false);

    const loadOnb = useCallback((refresh = false) => onboardingService.get(refresh).then((o) => {
        setOnb(o);
        if (o?.welcome) setWelcomeOpen(true);
    }).catch(() => setOnb(null)), []);
    useEffect(() => {
        loadOnb();
        let alive = true;
        onboardingService.trialEligible().then((v) => { if (alive) setTrial(!!v); });
        return () => { alive = false; };
    }, [uid, loadOnb]);
    const onbVisible = !!onb?.show;
    const markOnb = (action, product) => onboardingService.mark(action, product).then((o) => { if (o) setOnb(o); }).catch(() => {});
    const setFocus = (product) => markOnb('focus', product);
    const pickStart = (p) => { setFocus(p.key); navigate(userPath(p.to)); };
    // sequential: both writes rewrite the same preference row
    const closeWelcome = (focus) => {
        setWelcomeOpen(false);
        (focus ? onboardingService.mark('focus', focus) : Promise.resolve()).catch(() => {}).then(() => markOnb('welcome_seen'));
    };
    const resendEmail = async () => {
        const r = await authService.resendVerification(currentUser?.email);
        if (r?.success === false) message.error(r.message || t('Operation failed'));
        else message.success(t('Verification email sent'));
    };

    // server layout is the source (it adds product cards on first ownership); local copy = fast first paint / fallback
    useEffect(() => {
        let alive = true;
        setLayout(mergeLayout(readLocal(uid)));
        dashboardService.getLayout().then((r) => {
            if (!alive) return;
            setPersisted(!!r.persisted);
            if (r.layout) {
                const l = mergeLayout(r.layout);
                setLayout(l);
                writeLocal(uid, l);
            }
        }).catch(() => {});
        return () => { alive = false; };
    }, [uid]);

    const current = editing ? draft : layout;
    const shown = current.widgets.filter((w) => WIDGETS[w.key] && (w.key !== 'onboarding' || onbVisible));
    const { state, load } = useWidgetData(shown, cdnRange);

    const changeRange = (r) => {
        setCdnRange(r);
        const w = shown.find((x) => x.key === 'cdn');
        if (w) load([w], { range: r });
    };

    const refreshAll = () => { load(shown, { refresh: true, range: cdnRange }); loadOnb(true); };

    const save = async (next, { silent = false } = {}) => {
        writeLocal(uid, next);
        let r = { persisted: false };
        try {
            r = await dashboardService.saveLayout(next);
        } catch (e) {
            r = { persisted: false };
        }
        setPersisted(!!r.persisted);
        if (!silent) message.success(r.persisted ? t('Layout saved') : t('Layout saved in this browser'));
        return r;
    };

    // view switch outside edit mode: apply now, persist quietly (debounced)
    const setView = (key, view) => {
        const apply = (l) => ({ ...l, widgets: l.widgets.map((w) => (w.key === key ? { ...w, view } : w)) });
        if (editing) {
            setDraft((d) => apply(d));
            return;
        }
        setLayout((l) => {
            const next = apply(l);
            clearTimeout(viewSave.current);
            viewSave.current = setTimeout(() => save(next, { silent: true }), 600);
            return next;
        });
    };
    useEffect(() => () => clearTimeout(viewSave.current), []);

    // ---------------------------------------------------------------- edit mode
    const startEdit = () => { setDraft(mergeLayout(layout)); setEditing(true); };
    const cancelEdit = () => { setEditing(false); setDraft(null); };
    const patch = (key, p) => setDraft((d) => ({ ...d, widgets: d.widgets.map((w) => (w.key === key ? { ...w, ...p } : w)) }));
    const removeCard = (key) => setDraft((d) => ({ ...d, widgets: d.widgets.filter((w) => w.key !== key) }));
    const addCard = (key) => setDraft((d) => ({ ...d, widgets: [...d.widgets, { key, size: DEFAULT_SIZE[key], ...(VIEWS[key] ? { view: VIEWS[key][0][0] } : {}) }] }));
    const move = (key, delta) => setDraft((d) => {
        const ws = [...d.widgets];
        const i = ws.findIndex((w) => w.key === key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= ws.length) return d;
        [ws[i], ws[j]] = [ws[j], ws[i]];
        return { ...d, widgets: ws };
    });
    const dropOn = (targetKey) => {
        if (!dragKey || dragKey === targetKey) return;
        setDraft((d) => {
            const ws = [...d.widgets];
            const from = ws.findIndex((w) => w.key === dragKey);
            const [item] = ws.splice(from, 1);
            const to = ws.findIndex((w) => w.key === targetKey);
            ws.splice(from <= to ? to + 1 : to, 0, item);
            return { ...d, widgets: ws };
        });
    };

    const persist = async () => {
        setSaving(true);
        try {
            const r = await save(draft);
            setLayout(mergeLayout(r.layout || draft));
            setEditing(false);
            setDraft(null);
        } finally {
            setSaving(false);
        }
    };

    const resetDefault = async () => {
        setSaving(true);
        try {
            const r = await dashboardService.saveLayout(null);
            const l = mergeLayout(r.layout);
            writeLocal(uid, r.layout ? l : null);
            setLayout(l);
            setEditing(false);
            setDraft(null);
        } catch (e) {
            message.error(t('Operation failed'));
        } finally {
            setSaving(false);
        }
    };

    const addable = editing ? ALL_KEYS.filter((k) => !draft.widgets.some((w) => w.key === k) && (k !== 'onboarding' || onbVisible)) : [];

    // ---------------------------------------------------------------- render
    const ctx = { cdnRange, setCdnRange: changeRange, onboarding: onb, trial, resendEmail, setFocus };
    const spans = editing ? null : packSpans(shown, vw);
    const cells = shown.map((w, ri) => {
        const def = WIDGETS[w.key];
        const st = state[w.key];
        const idx = current.widgets.findIndex((x) => x.key === w.key);
        const isEmpty = !!(def.product && def.empty && st?.ok && st.data && def.empty(st.data));
        const extra = editing ? (
            <Tooltip title={t('Remove')}><Button size="small" type="text" icon={<DeleteOutlined />} onClick={() => removeCard(w.key)} aria-label={t('Remove')} /></Tooltip>
        ) : (
            <Space size={2}>
                {!isEmpty ? <ViewSwitch w={w} vw={vw} onChange={(v) => setView(w.key, v)} /> : null}
                {w.key === 'cdn' && !isEmpty ? (
                    <Segmented size="small" value={cdnRange} onChange={changeRange} options={['24h', '7d']} />
                ) : null}
                {st?.stale ? <Tooltip title={t('Source unavailable; showing the last data')}><span className="dw-stale" /></Tooltip> : null}
                {def.link ? <Link to={userPath(def.link)} className="dw-more" aria-label={t('View all')}><RightOutlined /></Link> : null}
                {w.key === 'onboarding' ? (
                    <Tooltip title={t('Hide')}><Button size="small" type="text" icon={<CloseOutlined />} onClick={() => markOnb('dismiss')} aria-label={t('Hide')} /></Tooltip>
                ) : null}
            </Space>
        );
        return (
            <div
                key={w.key}
                className={`dw-cell size-${w.size}${editing ? ' is-editing' : ''}${overKey === w.key && dragKey && dragKey !== w.key ? ' is-over' : ''}${dragKey === w.key ? ' is-dragging' : ''}`}
                style={spans ? { gridColumn: `span ${spans[ri]}` } : undefined}
                draggable={editing}
                onDragStart={(e) => { if (!editing) return; setDragKey(w.key); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', w.key); } catch (err) { /* noop */ } }}
                onDragOver={(e) => { if (!editing || !dragKey) return; e.preventDefault(); if (overKey !== w.key) setOverKey(w.key); }}
                onDrop={(e) => { e.preventDefault(); dropOn(w.key); setDragKey(null); setOverKey(null); }}
                onDragEnd={() => { setDragKey(null); setOverKey(null); }}
            >
                <Card
                    size="small"
                    className="dw-card"
                    title={(
                        <span className="dw-title">
                            {editing ? <HolderOutlined className="dw-handle" aria-label={t('Drag to reorder')} /> : null}
                            {t(WIDGET_TITLES[w.key])}
                        </span>
                    )}
                    extra={extra}
                >
                    {editing ? (
                        <div className="dw-editbar">
                            <Segmented
                                size="small"
                                value={w.size}
                                onChange={(v) => patch(w.key, { size: v })}
                                options={SIZES.map((s) => ({ label: t(SIZE_LABEL[s]), value: s }))}
                            />
                            <Space size={2}>
                                <Tooltip title={t('Move up')}><Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={idx === 0} onClick={() => move(w.key, -1)} /></Tooltip>
                                <Tooltip title={t('Move down')}><Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={idx === current.widgets.length - 1} onClick={() => move(w.key, 1)} /></Tooltip>
                            </Space>
                        </div>
                    ) : null}
                    <WidgetBoundary resetKey={tokenOf(w)}>
                        <WidgetBody w={w} st={st} ctx={ctx} onRetry={() => load([w], { refresh: true, range: cdnRange })} />
                    </WidgetBoundary>
                </Card>
            </div>
        );
    });

    return (
        <div className="dash-v2">
            <PageHeader
                title={t('Welcome, {{username}}', { username: currentUser?.username || t('User') })}
                dense
                extra={editing ? (
                    <Space wrap>
                        {addable.length ? (
                            <Dropdown trigger={['click']} menu={{ items: addable.map((k) => ({ key: k, label: t(WIDGET_TITLES[k]) })), onClick: ({ key }) => addCard(key) }}>
                                <Button icon={<PlusOutlined />}>{t('Add card')}</Button>
                            </Dropdown>
                        ) : null}
                        <Tooltip title={t('Rebuild from the products you use')}><Button icon={<UndoOutlined />} onClick={resetDefault} loading={saving}>{t('Restore defaults')}</Button></Tooltip>
                        <Button icon={<CloseOutlined />} onClick={cancelEdit}>{t('Cancel')}</Button>
                        <Button type="primary" icon={<CheckOutlined />} loading={saving} onClick={persist}>{t('Save layout')}</Button>
                    </Space>
                ) : (
                    <Space wrap>
                        <Tooltip title={t('Refresh data')}><Button icon={<ReloadOutlined />} onClick={refreshAll} aria-label={t('Refresh')} /></Tooltip>
                        <Button icon={<EditOutlined />} onClick={startEdit}>{t('Edit layout')}</Button>
                    </Space>
                )}
            />
            {editing ? (
                <div className="dw-edit-hint">
                    <Text type="secondary">{t('Drag cards to reorder and pick a width.')}{persisted ? '' : t('The layout is saved in this browser.')}</Text>
                </div>
            ) : null}
            {!editing && onb?.nudge && <StartStrip onPick={pickStart} onClose={() => markOnb('nudge_dismiss')} />}
            {!editing && <CampaignBanner />}
            {!editing && <AutoRenewNudge onEnabled={refreshAll} />}
            <div className="dash-grid">{cells}</div>
            <WelcomeModal open={welcomeOpen && !editing} onDone={closeWelcome} onFocus={setFocus} />
        </div>
    );
};

export default Dashboard;
